import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { admissionsAPI, wardsAPI, bedsAPI, doctorAPI, ipdReferralsAPI, patientsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  UserPlus,
  BedDouble,
  Clock,
  FileText,
  Search,
  ClipboardList,
  LogOut,
  Users,
  Stethoscope,
  ArrowRightLeft,
  ArrowRightCircle,
} from 'lucide-react';

const BED_TYPE_LABELS = {
  general: 'General Ward',
  semi_private: 'Semi-Private',
  private: 'Private Room',
  icu: 'ICU',
  isolation: 'Isolation',
};

const formatDate = (iso) => {
  if (!iso) return 'N/A';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const stayDuration = (admittedAt, dischargedAt) => {
  const start = new Date(admittedAt);
  const end = dischargedAt ? new Date(dischargedAt) : new Date();
  const days = Math.max(1, Math.ceil((end - start) / 86400000));
  return `${days} day${days > 1 ? 's' : ''}`;
};

export const AdmissionsPage = () => {
  const { user, hasPermission } = useAuth();
  const userRoleCode = (user?.role_code || '').toLowerCase();
  // v35: perm-based. The 'Admit Patient' button only shows for roles granted
  // 'admissions.create' in the Roles & Permissions matrix.
  const canAdmit = !!user?.is_super_admin || hasPermission('admissions.create');
  const canDischarge = !!user?.is_super_admin || hasPermission('discharge.perform');
  const isNurse = userRoleCode === 'nurse';
  const [admissions, setAdmissions] = useState([]);
  const [wards, setWards] = useState([]);
  const [availableBeds, setAvailableBeds] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tabFilter, setTabFilter] = useState('admitted');

  // Admit form
  const [isAdmitOpen, setIsAdmitOpen] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [admitForm, setAdmitForm] = useState({ patient_id: '', patient_name: '', ward_id: '', bed_id: '', doctor_id: '', diagnosis: '', treatment_notes: '', admission_type: 'inpatient', referral_id: null });
  const [activeReferral, setActiveReferral] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Discharge dialog
  const [isDischargeOpen, setIsDischargeOpen] = useState(false);
  const [dischargeAdmission, setDischargeAdmission] = useState(null);
  const [dischargeForm, setDischargeForm] = useState({ discharge_summary: '', discharge_instructions: '' });
  const [discharging, setDischarging] = useState(false);

  // Transfer dialog
  const [isTransferOpen, setIsTransferOpen] = useState(false);
  const [transferAdmission, setTransferAdmission] = useState(null);
  const [transferForm, setTransferForm] = useState({ new_ward_id: '', new_bed_id: '', reason: '' });
  const [transferBeds, setTransferBeds] = useState([]);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => { fetchData(); }, []);

  // Bed-Status Dashboard hands us ?ward=&bed=&bedNum=&wardName= when the user clicks an
  // Available bed there. Auto-open the admit dialog with that ward + bed pre-selected so the
  // nurse / FD lands directly on the patient-search step.
  const location = useLocation();
  const navigate = useNavigate();
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current) return;
    const params = new URLSearchParams(location.search);
    const wardId = params.get('ward');
    const bedId = params.get('bed');
    const referralId = params.get('referral');
    const patientId = params.get('patient');

    // v37b2: pre-fill from OPD → IPD referral
    if (referralId && patientId && !wardId) {
      prefilledRef.current = true;
      (async () => {
        try {
          const [refRes, patRes] = await Promise.all([
            ipdReferralsAPI.get(referralId),
            patientsAPI.get(patientId),
          ]);
          const ref = refRes.data; const pat = patRes.data;
          setActiveReferral(ref);
          setAdmitForm(f => ({
            ...f,
            patient_id: pat.id,
            patient_name: `${pat.first_name || ''} ${pat.last_name || ''}`.trim(),
            diagnosis: ref.diagnosis_summary || '',
            treatment_notes: ref.notes || '',
            admission_type: ref.suggested_admission_type || 'inpatient',
            referral_id: ref.id,
          }));
          setIsAdmitOpen(true);
          toast.info(`Admitting ${pat.first_name} (Referral from ${ref.referred_by_doctor_name})`);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Failed to load referral');
        }
        navigate(location.pathname, { replace: true });
      })();
      return;
    }

    if (!wardId || !bedId) return;
    prefilledRef.current = true;
    const bedNum = params.get('bedNum') || 'bed';
    const wardName = params.get('wardName') || 'ward';
    setAdmitForm(f => ({ ...f, ward_id: wardId, bed_id: bedId }));
    fetchAvailableBeds(wardId);
    setIsAdmitOpen(true);
    toast.success(`Admitting to ${wardName} — ${bedNum}`);
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const fetchData = async () => {
    try {
      const [admRes, wardsRes, docsRes] = await Promise.all([
        admissionsAPI.list(),
        wardsAPI.list(),
        doctorAPI.list().catch(() => ({ data: [] })),
      ]);
      setAdmissions(admRes.data || []);
      setWards(wardsRes.data || []);
      setDoctors(docsRes.data || []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableBeds = async (wardId) => {
    try {
      const res = await bedsAPI.list({ ward_id: wardId, status: 'available' });
      setAvailableBeds(res.data || []);
    } catch {
      setAvailableBeds([]);
    }
  };

  const searchPatients = async (query) => {
    if (query.length < 2) { setPatientResults([]); return; }
    setSearching(true);
    try {
      const { default: api } = await import('../lib/api');
      const res = await api.get('/patients/', { params: { search: query } });
      const patients = (res.data || []).map(p => ({
        ...p,
        name: `${p.first_name || ''} ${p.last_name || ''}`.trim(),
      }));
      setPatientResults(patients);
    } catch {
      setPatientResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleAdmit = async (e) => {
    e.preventDefault();
    if (!admitForm.patient_id || !admitForm.ward_id || !admitForm.bed_id || !admitForm.doctor_id) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await admissionsAPI.create(admitForm);
      toast.success('Patient admitted successfully');
      setIsAdmitOpen(false);
      resetAdmitForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to admit patient');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDischarge = async (e) => {
    e.preventDefault();
    setDischarging(true);
    try {
      const res = await admissionsAPI.discharge(dischargeAdmission.id, dischargeForm);
      const inv = res.data?.invoice;
      toast.success(`Patient discharged. Invoice ${inv?.invoice_number || ''} generated: Rs. ${inv?.total_amount || 0}`);
      setIsDischargeOpen(false);
      setDischargeAdmission(null);
      setDischargeForm({ discharge_summary: '', discharge_instructions: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to discharge');
    } finally {
      setDischarging(false);
    }
  };

  const openDischarge = (adm) => {
    setDischargeAdmission(adm);
    setDischargeForm({ discharge_summary: adm.diagnosis ? `Diagnosis: ${adm.diagnosis}` : '', discharge_instructions: '' });
    setIsDischargeOpen(true);
  };

  const openTransfer = (adm) => {
    setTransferAdmission(adm);
    setTransferForm({ new_ward_id: '', new_bed_id: '', reason: '' });
    setTransferBeds([]);
    setIsTransferOpen(true);
  };

  const handleTransferWardChange = async (wardId) => {
    setTransferForm({ ...transferForm, new_ward_id: wardId, new_bed_id: '' });
    try {
      const res = await bedsAPI.list({ ward_id: wardId, status: 'available' });
      setTransferBeds(res.data || []);
    } catch {
      setTransferBeds([]);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.new_ward_id || !transferForm.new_bed_id) {
      toast.error('Please select ward and bed');
      return;
    }
    setTransferring(true);
    try {
      await admissionsAPI.transfer(transferAdmission.id, transferForm);
      const newWard = wards.find(w => w.id === transferForm.new_ward_id);
      toast.success(`Patient transferred to ${newWard?.name || 'new ward'}`);
      setIsTransferOpen(false);
      setTransferAdmission(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const resetAdmitForm = () => {
    setAdmitForm({ patient_id: '', patient_name: '', ward_id: '', bed_id: '', doctor_id: '', diagnosis: '', treatment_notes: '', admission_type: 'inpatient', referral_id: null });
    setActiveReferral(null);
    setPatientSearch('');
    setPatientResults([]);
    setAvailableBeds([]);
  };

  const handleWardChange = (wardId) => {
    setAdmitForm({ ...admitForm, ward_id: wardId, bed_id: '' });
    fetchAvailableBeds(wardId);
  };

  const filtered = admissions.filter(a => {
    if (tabFilter === 'admitted' && a.status !== 'admitted') return false;
    if (tabFilter === 'discharged' && a.status !== 'discharged') return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      a.patient?.name?.toLowerCase().includes(q) ||
      a.patient?.patient_id?.toLowerCase().includes(q) ||
      a.admission_number?.toLowerCase().includes(q) ||
      a.bed?.bed_number?.toLowerCase().includes(q)
    );
  });

  const activeCount = admissions.filter(a => a.status === 'admitted').length;
  const dischargedCount = admissions.filter(a => a.status === 'discharged').length;

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admissions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            IPD Admissions
          </h1>
          <p className="mt-1 text-slate-600">
            {activeCount} active admission{activeCount !== 1 ? 's' : ''} | {dischargedCount} discharged
          </p>
        </div>
        <Dialog open={isAdmitOpen} onOpenChange={(v) => { setIsAdmitOpen(v); if (!v) resetAdmitForm(); }}>
          {canAdmit ? (
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="admit-patient-btn">
                <UserPlus className="h-4 w-4 mr-2" /> Admit Patient
              </Button>
            </DialogTrigger>
          ) : (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="nurse-admit-notice">
              {isNurse ? 'Nurses cannot finalise admissions — go to Nurse Workstation to temp-reserve a bed.' : 'Only Front Desk / Billing / Admin can admit patients.'}
            </div>
          )}
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Admit Patient</DialogTitle>
              <DialogDescription>Assign a bed and admit a patient for inpatient care</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdmit}>
              <div className="space-y-4 py-4">
                {/* Patient Search */}
                <div className="space-y-2">
                  <Label>Patient *</Label>
                  {admitForm.patient_id ? (
                    <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <span className="font-medium text-emerald-800">{admitForm.patient_name}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setAdmitForm({ ...admitForm, patient_id: '', patient_name: '' })}>Change</Button>
                    </div>
                  ) : (
                    <div>
                      <Input
                        placeholder="Search patient by name or ID..."
                        value={patientSearch}
                        onChange={e => { setPatientSearch(e.target.value); searchPatients(e.target.value); }}
                        data-testid="patient-search-input"
                      />
                      {patientResults.length > 0 && (
                        <div className="mt-1 border rounded-lg max-h-40 overflow-y-auto bg-white shadow-sm">
                          {patientResults.map(p => (
                            <button
                              key={p.id}
                              type="button"
                              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                              onClick={() => {
                                setAdmitForm({ ...admitForm, patient_id: p.id, patient_name: `${p.name} (${p.patient_id})` });
                                setPatientResults([]);
                                setPatientSearch('');
                              }}
                              data-testid={`patient-option-${p.id}`}
                            >
                              {p.name} — {p.patient_id} | {p.phone}
                            </button>
                          ))}
                        </div>
                      )}
                      {searching && <p className="text-sm text-slate-400 mt-1">Searching...</p>}
                    </div>
                  )}
                </div>

                {/* Ward + Bed */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ward *</Label>
                    <Select value={admitForm.ward_id} onValueChange={handleWardChange}>
                      <SelectTrigger data-testid="ward-select"><SelectValue placeholder="Select ward" /></SelectTrigger>
                      <SelectContent>
                        {wards.filter(w => w.available_beds > 0).map(w => (
                          <SelectItem key={w.id} value={w.id}>
                            {w.name} ({BED_TYPE_LABELS[w.bed_type]} — {w.available_beds} free)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Bed *</Label>
                    <Select value={admitForm.bed_id} onValueChange={v => setAdmitForm({ ...admitForm, bed_id: v })} disabled={!admitForm.ward_id}>
                      <SelectTrigger data-testid="bed-select"><SelectValue placeholder="Select bed" /></SelectTrigger>
                      <SelectContent>
                        {availableBeds.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.bed_number}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Doctor */}
                <div className="space-y-2">
                  <Label>Attending Doctor *</Label>
                  <Select value={admitForm.doctor_id} onValueChange={v => setAdmitForm({ ...admitForm, doctor_id: v })}>
                    <SelectTrigger data-testid="doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                    <SelectContent>
                      {doctors.map(d => (
                        <SelectItem key={d.id} value={d.user_id}>
                          {d.user?.first_name} {d.user?.last_name} — {d.specialization}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Admission Type + Referral context */}
                {activeReferral && (
                  <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs space-y-1">
                    <p className="font-semibold text-sky-800 flex items-center gap-1"><ArrowRightCircle size={13} /> OPD Referral</p>
                    <p className="text-sky-700">Referred by <strong>{activeReferral.referred_by_doctor_name}</strong> · {new Date(activeReferral.referred_at).toLocaleString('en-IN')}</p>
                    <p className="text-sky-700"><strong>Reason:</strong> {activeReferral.clinical_reason}</p>
                    {activeReferral.diagnosis_summary && <p className="text-sky-700"><strong>Diagnosis:</strong> {activeReferral.diagnosis_summary}</p>}
                    <p className="text-sky-700">Urgency: <span className="font-semibold uppercase">{activeReferral.urgency}</span></p>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Admission Type *</Label>
                  <div className="flex gap-2">
                    {[{v:'inpatient',l:'Inpatient'},{v:'day_care',l:'Day Care'}].map(opt => (
                      <button key={opt.v} type="button"
                        onClick={() => setAdmitForm({ ...admitForm, admission_type: opt.v })}
                        className={`flex-1 py-2 px-3 text-sm font-semibold rounded-lg border transition ${admitForm.admission_type === opt.v ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
                        data-testid={`adm-type-${opt.v}`}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                  {admitForm.admission_type === 'day_care' && (
                    <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">Day Care patient — expected discharge auto-set from ward's "Day Care Max Hours".</p>
                  )}
                </div>

                {/* Diagnosis & Notes */}
                <div className="space-y-2">
                  <Label>Diagnosis</Label>
                  <Input value={admitForm.diagnosis} onChange={e => setAdmitForm({ ...admitForm, diagnosis: e.target.value })} placeholder="Initial diagnosis" data-testid="diagnosis-input" />
                </div>
                <div className="space-y-2">
                  <Label>Treatment Notes</Label>
                  <textarea
                    className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm"
                    value={admitForm.treatment_notes}
                    onChange={e => setAdmitForm({ ...admitForm, treatment_notes: e.target.value })}
                    placeholder="Initial treatment plan..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsAdmitOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting} data-testid="admit-submit-btn">
                  {submitting ? 'Admitting...' : 'Admit Patient'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search + Tabs */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by patient name, ID, admission number, or bed..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="admission-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Tabs value={tabFilter} onValueChange={setTabFilter} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="admitted" data-testid="tab-admitted">
            <BedDouble className="h-4 w-4 mr-2" />
            Active ({activeCount})
          </TabsTrigger>
          <TabsTrigger value="discharged" data-testid="tab-discharged">
            <ClipboardList className="h-4 w-4 mr-2" />
            Discharged ({dischargedCount})
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-admissions">
            <Users className="h-4 w-4 mr-2" />
            All ({admissions.length})
          </TabsTrigger>
        </TabsList>

        {['admitted', 'discharged', 'all'].map(tab => (
          <TabsContent key={tab} value={tab}>
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <BedDouble className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No {tab === 'all' ? '' : tab} admissions found</p>
              </div>
            ) : (
              <div className="grid gap-3">
                {filtered.map(a => (
                  <Card key={a.id} className="hover:border-emerald-200 transition-colors" data-testid={`admission-card-${a.id}`}>
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900">{a.patient?.name || 'Unknown'}</h3>
                            <Badge variant="outline" className="text-xs">{a.admission_number}</Badge>
                            <Badge className={a.status === 'admitted' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}>
                              {a.status === 'admitted' ? 'Active' : 'Discharged'}
                            </Badge>
                            {a.admission_type === 'day_care' && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Day Care{a.expected_discharge_at ? ` · expected ${new Date(a.expected_discharge_at).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}` : ''}</Badge>
                            )}
                            {a.referral_id && a.referred_by_doctor_name && (
                              <Badge className="bg-sky-100 text-sky-700 text-xs inline-flex items-center gap-1"><ArrowRightCircle size={10} /> OPD Referral · {a.referred_by_doctor_name}</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Ward / Bed</p>
                              <p className="font-medium">{a.ward?.name} — {a.bed?.bed_number}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Bed Type</p>
                              <p className="font-medium">{BED_TYPE_LABELS[a.ward?.bed_type] || a.ward?.bed_type}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Doctor</p>
                              <p className="font-medium">{a.doctor?.first_name} {a.doctor?.last_name}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Admitted</p>
                              <p className="font-medium">{formatDate(a.admitted_at)}</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Stay</p>
                              <p className="font-medium">{stayDuration(a.admitted_at, a.discharged_at)}</p>
                            </div>
                          </div>
                          {a.diagnosis && (
                            <p className="mt-2 text-sm text-slate-600"><span className="text-slate-500">Diagnosis:</span> {a.diagnosis}</p>
                          )}
                          {a.discharge_summary && (
                            <div className="mt-2 p-3 bg-slate-50 rounded-lg text-sm">
                              <p className="font-medium text-slate-700 mb-1">Discharge Summary</p>
                              <p className="text-slate-600">{a.discharge_summary}</p>
                              {a.discharge_instructions && (
                                <p className="mt-1 text-slate-500"><span className="font-medium">Instructions:</span> {a.discharge_instructions}</p>
                              )}
                            </div>
                          )}
                          {a.transfer_history?.length > 0 && (
                            <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm">
                              <p className="font-medium text-blue-700 mb-1">Transfer History ({a.transfer_history.length})</p>
                              {a.transfer_history.map((tr, i) => (
                                <p key={i} className="text-blue-600 text-xs">
                                  {tr.from_ward_name} ({tr.from_bed_number}) &rarr; {tr.to_ward_name} ({tr.to_bed_number})
                                  {tr.reason && <span className="text-blue-400"> — {tr.reason}</span>}
                                  <span className="text-blue-400 ml-2">{formatDate(tr.transferred_at)}</span>
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                        {a.status === 'admitted' && (canAdmit || canDischarge) && (
                          <div className="flex flex-col gap-2 ml-4">
                            {canAdmit && (
                              <Button
                                variant="outline"
                                className="border-blue-200 text-blue-600 hover:bg-blue-50"
                                onClick={() => openTransfer(a)}
                                data-testid={`transfer-btn-${a.id}`}
                              >
                                <ArrowRightLeft className="h-4 w-4 mr-2" />
                                Transfer
                              </Button>
                            )}
                            {canDischarge && (
                              <Button
                                variant="outline"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => openDischarge(a)}
                                data-testid={`discharge-btn-${a.id}`}
                              >
                                <LogOut className="h-4 w-4 mr-2" />
                                Discharge
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Discharge Dialog */}
      <Dialog open={isDischargeOpen} onOpenChange={setIsDischargeOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Discharge Patient</DialogTitle>
            <DialogDescription>
              {dischargeAdmission && (
                <span>{dischargeAdmission.patient?.name} — {dischargeAdmission.bed?.bed_number} — Stay: {stayDuration(dischargeAdmission.admitted_at)}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDischarge}>
            <div className="space-y-4 py-4">
              {dischargeAdmission && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
                  An invoice will be auto-generated based on {stayDuration(dischargeAdmission.admitted_at)} stay at Rs. {dischargeAdmission.ward?.charge_per_day}/day
                  {dischargeAdmission.transfer_history?.length > 0 && (
                    <span className="block mt-1">Billing will be split across {dischargeAdmission.transfer_history.length + 1} ward segments with different rates.</span>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label>Discharge Summary *</Label>
                <textarea
                  className="w-full min-h-[100px] px-3 py-2 rounded-md border border-input bg-background text-sm"
                  value={dischargeForm.discharge_summary}
                  onChange={e => setDischargeForm({ ...dischargeForm, discharge_summary: e.target.value })}
                  placeholder="Summary of treatment, findings, and outcome..."
                  required
                  data-testid="discharge-summary-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Discharge Instructions</Label>
                <textarea
                  className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm"
                  value={dischargeForm.discharge_instructions}
                  onChange={e => setDischargeForm({ ...dischargeForm, discharge_instructions: e.target.value })}
                  placeholder="Post-discharge care instructions, medications, follow-up..."
                  data-testid="discharge-instructions-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDischargeOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white" disabled={discharging} data-testid="discharge-submit-btn">
                {discharging ? 'Processing...' : 'Discharge & Generate Invoice'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={isTransferOpen} onOpenChange={setIsTransferOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Transfer Patient</DialogTitle>
            <DialogDescription>
              {transferAdmission && (
                <span>Move {transferAdmission.patient?.name} from {transferAdmission.ward?.name} — {transferAdmission.bed?.bed_number} to a new ward/bed</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTransfer}>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800">
                Billing will auto-adjust: each ward segment is billed at its own rate per day.
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>New Ward *</Label>
                  <Select value={transferForm.new_ward_id} onValueChange={handleTransferWardChange}>
                    <SelectTrigger data-testid="transfer-ward-select"><SelectValue placeholder="Select ward" /></SelectTrigger>
                    <SelectContent>
                      {wards.filter(w => w.available_beds > 0).map(w => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.name} ({BED_TYPE_LABELS[w.bed_type]} — {w.available_beds} free)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>New Bed *</Label>
                  <Select value={transferForm.new_bed_id} onValueChange={v => setTransferForm({ ...transferForm, new_bed_id: v })} disabled={!transferForm.new_ward_id}>
                    <SelectTrigger data-testid="transfer-bed-select"><SelectValue placeholder="Select bed" /></SelectTrigger>
                    <SelectContent>
                      {transferBeds.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.bed_number}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Reason for Transfer</Label>
                <Input
                  value={transferForm.reason}
                  onChange={e => setTransferForm({ ...transferForm, reason: e.target.value })}
                  placeholder="e.g., Patient condition improved, moving from ICU to General Ward"
                  data-testid="transfer-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsTransferOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={transferring} data-testid="transfer-submit-btn">
                {transferring ? 'Transferring...' : 'Transfer Patient'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdmissionsPage;
